import { councilDashboardApi } from "@/lib/api"
import type {
  AdminManufacturerFleetHealth,
  ApiResponseDashboardInfoDTO,
  DashboardInfoDTO,
  Stats,
} from "@/gen/api"
import { useQuery } from "@tanstack/react-query"

function safeNumber(value: unknown): number | undefined {
  if (typeof value === "number") return value
  if (value == null) return undefined
  if (typeof value === "object" && "count" in value) return (value as { count: number }).count
  return undefined
}

function sanitizeStats(stats: Stats): Stats {
  const result: Stats = {}
  const devices = safeNumber(stats.devices)
  if (devices !== undefined) result.devices = devices
  const activeDevices = safeNumber(stats.activeDevices)
  if (activeDevices !== undefined) result.activeDevices = activeDevices
  const customers = safeNumber(stats.customers)
  if (customers !== undefined) result.customers = customers
  const waterConsumption = safeNumber(stats.waterConsumption)
  if (waterConsumption !== undefined) result.waterConsumption = waterConsumption
  const activeAlarms = safeNumber(stats.activeAlarms)
  if (activeAlarms !== undefined) result.activeAlarms = activeAlarms
  const alertDeviceCount = safeNumber(stats.alertDeviceCount)
  if (alertDeviceCount !== undefined) result.alertDeviceCount = alertDeviceCount
  const leakAlarms = safeNumber(stats.leakAlarms)
  if (leakAlarms !== undefined) result.leakAlarms = leakAlarms
  if (typeof stats.waterEfficiency === "string") result.waterEfficiency = stats.waterEfficiency
  if (typeof stats.peakUsageTime === "string") result.peakUsageTime = stats.peakUsageTime
  if (typeof stats.monthlySavings === "string") result.monthlySavings = stats.monthlySavings
  if (typeof stats.lastSync === "string") result.lastSync = stats.lastSync
  return result
}

function sanitizeManufacturerHealth(
  manufacturerHealth: AdminManufacturerFleetHealth[] | undefined,
): AdminManufacturerFleetHealth[] {
  if (!Array.isArray(manufacturerHealth)) return []

  return manufacturerHealth.map((item) => ({
    manufacturerId: item.manufacturerId,
    manufacturerName: item.manufacturerName,
    totalCount: safeNumber(item.totalCount) ?? 0,
    onlineCount: safeNumber(item.onlineCount) ?? 0,
    offlineCount: safeNumber(item.offlineCount) ?? 0,
    healthPercentage: safeNumber(item.healthPercentage) ?? 0,
  }))
}

const useGetDashboard = (councilId: number) => {
  return useQuery({
    queryKey: [useGetDashboard.QUERY_KEY, councilId],
    queryFn: async () => {
      const response = await councilDashboardApi.getCouncilDashboard(councilId)
      const body = response.data as ApiResponseDashboardInfoDTO
      if (!body.data) {
        throw new Error(body.message || "No dashboard data returned")
      }
      const data: DashboardInfoDTO = {
        ...body.data,
        ...(body.data.stats ? { stats: sanitizeStats(body.data.stats) } : {}),
        manufacturerHealth: sanitizeManufacturerHealth(body.data.manufacturerHealth),
      }
      return data
    },
    enabled: !!councilId,
  })
}

useGetDashboard.QUERY_KEY = "dashboard"

export default useGetDashboard
